import { useEffect, useState } from 'react';

import { getCategories } from '../services/firestoreService';
import { normalizeIdArray } from '../utils/collectionUtils';

export default function CategoryMultiSelect({ value = [], onChange, compact = false }) {
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchCategories = async () => {
      try {
        const data = await getCategories();

        if (!isMounted) {
          return;
        }

        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching categories:', error);

        if (isMounted) {
          setCategories([]);
        }
      }
    };

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    const nextFilteredCategories = categories.filter((category) =>
      String(category?.name || '')
        .toLowerCase()
        .includes(normalizedSearchTerm)
    );

    setFilteredCategories(nextFilteredCategories);
  }, [categories, searchTerm]);

  const selectedIds = normalizeIdArray(value);

  const handleToggleCategory = (categoryId) => {
    const isSelected = selectedIds.includes(categoryId);

    if (isSelected) {
      onChange?.(
        Array.from(new Set(selectedIds.filter((id) => id !== categoryId)))
      );
      return;
    }

    onChange?.(Array.from(new Set([...selectedIds, categoryId])));
  };

  const handleRemoveCategory = (categoryId) => {
    onChange?.(
      Array.from(new Set(selectedIds.filter((id) => id !== categoryId)))
    );
  };

  const selectedCategories = selectedIds
    .map((categoryId) =>
      categories.find((category) => category.id === categoryId) || null
    )
    .filter(Boolean);

  if (compact) {
    const buttonLabel = selectedIds.length
      ? `Category (${selectedIds.length})`
      : 'Category';

    return (
      <div className="category-select category-select--compact">
        <button
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          className="select category-select__trigger"
          aria-expanded={isOpen}
        >
          <span>{buttonLabel}</span>
          <span aria-hidden="true">v</span>
        </button>

        {isOpen ? (
          <div className="dropdown category-select__dropdown">
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search categories..."
              className="select category-select__search"
            />

            <div className="category-select__list">
              {filteredCategories.length > 0 ? (
                filteredCategories.map((category) => {
                  const isSelected = selectedIds.includes(category.id);

                  return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleToggleCategory(category.id)}
                      className={`dropdown-item${isSelected ? ' active' : ''}`}
                    >
                      {category.name || 'Unnamed Category'}
                      {isSelected ? ' [Selected]' : ''}
                    </button>
                  );
                })
              ) : (
                <div className="dropdown-item category-select__empty">No categories found.</div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="category-select">
      <input
        type="text"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        placeholder="Search categories..."
        className="select category-select__search"
      />

      <div className="category-select__tags">
        {selectedCategories.length > 0 ? (
          selectedCategories.map((category) => (
            <span key={category.id} className="tag category-select__tag">
              {category.name || 'Unnamed Category'}
              <button
                type="button"
                onClick={() => handleRemoveCategory(category.id)}
                className="category-select__remove"
                aria-label={`Remove ${category.name || 'category'}`}
              >
                x
              </button>
            </span>
          ))
        ) : (
          <span className="category-select__empty">No categories selected.</span>
        )}
      </div>

      <div className="dropdown category-select__dropdown">
        {filteredCategories.length > 0 ? (
          filteredCategories.map((category) => {
            const isSelected = selectedIds.includes(category.id);

            return (
              <button
                key={category.id}
                type="button"
                onClick={() => handleToggleCategory(category.id)}
                className={`dropdown-item${isSelected ? ' active' : ''}`}
              >
                {category.name || 'Unnamed Category'}
                {isSelected ? ' [Selected]' : ''}
              </button>
            );
          })
        ) : (
          <div className="dropdown-item category-select__empty">No categories found.</div>
        )}
      </div>
    </div>
  );
}
